import React, { useState, useEffect } from 'react'
import axios from 'axios'
import 'leaflet/dist/leaflet.css'
import MapView from '../components/shared/MapView'
import AddressAutocomplete from '../components/shared/AddressAutocomplete'
// import StyledButtonGoBack from '../components/shared/GoBackButton'
import styled from 'styled-components'
import { useQuery } from '@tanstack/react-query'
import { fetchDatabase } from '../utils/services/fetchDatabase'
import ItemImpact from '../components/shared/ItemImpact'
import { useLocation } from 'react-router-dom'
import Sidebar from '../components/shared/Sidebar'
import { fetchUserInfo } from '../utils/services/fetchUser'

const apiUrl =
    process.env.NODE_ENV === 'production'
        ? '/api'
        : process.env.REACT_APP_API_URL
const StyledNewProduct = styled.div`
    justify-content: center;
    margin: auto;
    display: flex;
    width: 100vw;
    max-width: 1500px;
`
const StyledForm = styled.form`
    padding: 40px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    div {
        display: flex;
        label {
            width: 200px;
        }
        div {
            display: flex;
            flex-direction: column;
        }
    }
`
const StyledCreateModifyContainer = styled.div`
    display: flex;
    justify-content: center;
`
const StyledModifyButton = styled.div`
    background-color: #1c8516;
    color: white;
    border-radius: 50px;
    padding: 10px 50px;
    margin: auto;
    transition: all 0.3s ease-in-out;
    &:hover {
        cursor: pointer;
        filter: brightness(0.8);
    }
`

const CategorySelector = ({
    categories,
    onCategoryChange,
    selectedCategories,
    level = 0,
}) => {
    const handleChange = (e) => {
        const selectedName = e.target.value
        const selectedCategory = categories.find(
            (cat) => cat.name === selectedName
        )
        onCategoryChange(level, selectedCategory)
    }

    const hasSubcategories = (category) => {
        return Object.keys(category || {}).some(
            (key) =>
                Array.isArray(category[key]) &&
                category[key].length > 0 &&
                key !== 'impact'
        )
    }

    const getSubcategories = (category) => {
        return Object.values(category || {}).find(
            (value) => Array.isArray(value) && value.length > 0 && value[0].name
        )
    }

    return (
        <div>
            <select
                value={selectedCategories[level]?.name || ''}
                onChange={handleChange}
                required
            >
                <option value="">Sélectionnez une catégorie</option>
                {categories.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                        {cat.name}
                    </option>
                ))}
            </select>

            {selectedCategories[level] &&
                hasSubcategories(selectedCategories[level]) && (
                    <CategorySelector
                        categories={getSubcategories(selectedCategories[level])}
                        onCategoryChange={onCategoryChange}
                        selectedCategories={selectedCategories}
                        level={level + 1}
                    />
                )}
        </div>
    )
}

const NewProduct = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const location = useLocation()
    const { thingInfo } = location.state || {}
    const [title, setTitle] = useState(thingInfo?.title || '')
    const [description, setDescription] = useState(thingInfo?.description || '')
    const [price, setPrice] = useState(thingInfo?.price || '0')
    const [isBio, setIsBio] = useState(thingInfo?.isBio || false)
    const [selectedFile, setSelectedFile] = useState(null)
    const [previewUrl, setPreviewUrl] = useState(thingInfo?.imageUrl || '')
    const [address, setAddress] = useState(null)
    const [isPerishable, setIsPerishable] = useState(false)
    const [expiringDate, setExpiringDate] = useState(
        thingInfo?.thingInfo ? thingInfo.thingInfo.split('T')[0] : ''
    )
    const [quantity, setQuantity] = useState(thingInfo?.quantity || '1')
    const [unit, setUnit] = useState('')
    const [impact, setImpact] = useState(null)

    const [selectedCategories, setSelectedCategories] = useState([])

    // Get user info
    const {
        data: userInfo,
        isLoading: isLoadingUserInfo,
        isError: isErrorUserInfo,
    } = useQuery({
        queryKey: ['userInfo'],
        queryFn: fetchUserInfo,
        staleTime: 5 * 60 * 1000,
        retry: 0,
        onError: (err) => {
            console.error('Error fetching user info:', err)
        },
    })

    const { data: stuffCategories } = useQuery({
        queryKey: ['categoryDatabase'],
        queryFn: fetchDatabase,
        staleTime: 60 * 60 * 1000,
        retry: 0,
        onError: (err) => {
            console.error('Error fetching stuff categories:', err)
        },
    })

    const handleFileChange = (e) => {
        const file = e.target.files[0]
        setSelectedFile(file)
        if (file) {
            setPreviewUrl(URL.createObjectURL(file))
        }
    }

    // Met à jour l'aperçu si thingInfo change
    useEffect(() => {
        if (thingInfo?.imageUrl) {
            setPreviewUrl(thingInfo.imageUrl)
        }
    }, [thingInfo])

    const handleCategoryChange = (level, category) => {
        const newSelectedCategories = [...selectedCategories]
        newSelectedCategories[level] = category

        // Réinitialisation des catégories sélectionnées au niveau inférieur
        if (category) {
            newSelectedCategories.splice(level + 1)
        } else {
            newSelectedCategories.splice(level)
        }

        setSelectedCategories(newSelectedCategories)

        // Mise à jour de l'unité lors de la sélection d'une catégorie
        updateUnit(newSelectedCategories)
        calculateImpact(quantity, price, newSelectedCategories) // Recalculer l'impact à chaque changement de catégorie
    }

    const updateUnit = (selectedCategories) => {
        if (selectedCategories.length === 0) {
            setUnit('')
            return
        }

        const lastSelectedCategory =
            selectedCategories[selectedCategories.length - 1]

        if (lastSelectedCategory && lastSelectedCategory.unit) {
            setUnit(lastSelectedCategory.unit)
        } else {
            for (let i = selectedCategories.length - 1; i >= 0; i--) {
                const category = selectedCategories[i]
                if (category && category.unit) {
                    setUnit(category.unit)
                    return
                }
            }
            setUnit('')
        }
    }

    const calculateImpact = (qty, price, selectedCategories) => {
        const deepestCategory =
            selectedCategories[selectedCategories.length - 1]
        if (deepestCategory?.impact) {
            // Assurez-vous que `impact` est une propriété de votre catégorie dans la réponse de l'API
            const { money, CO2, trash } = deepestCategory.impact[0] || {}

            if (money && CO2 && trash && price) {
                setImpact({
                    impactMoney: (qty * money).toFixed(2),
                    impactCO2: (qty * CO2).toFixed(3),
                    impactTrash: (qty * trash).toFixed(3),
                    price: parseFloat(price).toFixed(2),
                })
            } else {
                setImpact(null) // Réinitialiser si aucune donnée d'impact disponible
            }
        } else {
            setImpact(null) // Réinitialiser si aucune catégorie
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        const formData = new FormData()
        const lat = address.lat
        const lon = address.lon
        const addressText = address.display_name

        const deepestCategory =
            selectedCategories[selectedCategories.length - 1]

        const thingData = {
            title,
            description,
            price,
            lat,
            lon,
            addressText,
            category: deepestCategory?.name || '',
            expiringDate,
            isBio,
            quantity,
            unit, // Ajouter l'unité ici
        }

        formData.append('thing', JSON.stringify(thingData))

        if (selectedFile) {
            formData.append('image', selectedFile)
        }

        if (thingInfo) {
            // If we are trying to modify something
            try {
                const response = await axios.put(
                    `${apiUrl}/stuff/${thingInfo._id}`,
                    formData,
                    {
                        withCredentials: true,
                    }
                )
                console.log('Réponse du serveur:', response.data)
                window.location.replace('/Dashboard')
            } catch (error) {
                console.error(
                    'Erreur lors de la requête:',
                    error.response ? error.response.data : error.message
                )
            }
        } else {
            // If we are trying to create something
            try {
                const response = await axios.post(`${apiUrl}/stuff`, formData, {
                    withCredentials: true,
                })
                console.log('Réponse du serveur:', response.data)
                window.location.replace('/Dashboard')
            } catch (error) {
                console.error(
                    'Erreur lors de la requête:',
                    error.response ? error.response.data : error.message
                )
            }
        }
    }

    return (
        <StyledNewProduct>
            {userInfo && (
                <Sidebar
                    setIsSidebarOpen={setIsSidebarOpen}
                    isSidebarOpen={isSidebarOpen}
                    userInfo={userInfo}
                />
            )}
            {/* <StyledButtonGoBack /> */}
            <StyledForm onSubmit={handleSubmit}>
                <div>
                    <label>Titre (*)</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                    />
                </div>
                <div>
                    <label>Description (*)</label>
                    <input
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                    />
                </div>
                <div>
                    <label>Quantité (*)</label>
                    <input
                        type="number"
                        value={quantity}
                        onChange={(e) => {
                            const newValue = parseInt(e.target.value, 10)
                            if (newValue > 0 || e.target.value === '') {
                                setQuantity(e.target.value)
                                calculateImpact(
                                    e.target.value,
                                    price,
                                    selectedCategories
                                ) // Recalculer l'impact
                            }
                        }}
                        required
                    />
                    {/* Afficher l'unité automatiquement */}
                    <span>&nbsp;{unit && `${unit}`}</span>
                </div>

                <div>
                    <label>Catégorie (*)</label>
                    {stuffCategories && (
                        <CategorySelector
                            categories={stuffCategories}
                            onCategoryChange={handleCategoryChange}
                            selectedCategories={selectedCategories}
                        />
                    )}
                </div>

                <div>
                    <label>Bio</label>
                    <input
                        type="checkbox"
                        checked={isBio}
                        onChange={(e) => setIsBio(!isBio)}
                    />
                </div>
                <div>
                    <label>Prix total (*)</label>
                    <input
                        type="text"
                        value={price}
                        onChange={(e) => {
                            const newValue = parseFloat(e.target.value)
                            if (newValue >= 0 || e.target.value === '') {
                                setPrice(e.target.value)
                                calculateImpact(
                                    quantity,
                                    e.target.value,
                                    selectedCategories
                                ) // Recalculer l'impact
                            }
                        }}
                        required
                    />
                </div>
                <div>
                    <label>Périssable</label>
                    <input
                        type="checkbox"
                        checked={isPerishable}
                        onChange={(e) => setIsPerishable(!isPerishable)}
                    />
                </div>
                {isPerishable && (
                    <div>
                        <label>Date de péremption (*)</label>
                        <input
                            type="date"
                            value={expiringDate}
                            onChange={(e) => setExpiringDate(e.target.value)}
                            placeholder="Expiring date"
                        />
                    </div>
                )}
                <div>
                    <label>Image (*)</label>
                    <div>
                        <input
                            type="file"
                            onChange={handleFileChange}
                            accept="image/*"
                        />
                        {previewUrl && (
                            <div>
                                <img
                                    src={previewUrl}
                                    alt="Aperçu"
                                    style={{
                                        width: '100px',
                                        height: 'auto',
                                        margin: '10px',
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div>
                    <label>Localisation (*)</label>
                    <AddressAutocomplete setAddress={setAddress} />
                </div>

                {address && <MapView lat={address.lat} lon={address.lon} />}

                <StyledCreateModifyContainer>
                    {!title ||
                    !description ||
                    !price ||
                    !address ||
                    !previewUrl ||
                    !impact ? (
                        <div>
                            Remplissez toutes les champs obligatoires (*) pour
                            pouvoir valider les modifications
                        </div>
                    ) : (
                        <div>
                            <StyledModifyButton
                                type="submit"
                                onClick={handleSubmit}
                            >
                                {thingInfo ? 'Modifier' : 'Créer'}
                            </StyledModifyButton>
                        </div>
                    )}
                </StyledCreateModifyContainer>
            </StyledForm>
        </StyledNewProduct>
    )
}

export default NewProduct
